import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Mail, CheckCircle, ArrowLeft, Lock, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PageTransition } from '@/components/PageTransition';

const ForgotPasswordPages = () => {
  const navigate = useNavigate();
  usePageTitle('Forgot Password');
  const [currentStep, setCurrentStep] = useState(1); // 1: Email, 2: Check Email
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Use password-reset edge function with n8n email integration
      const { data: resetResponse, error: functionError } = await supabase.functions.invoke('password-reset', {
        body: {
          email: email
        }
      });

      if (functionError) {
        setError(functionError.message || 'Failed to send reset email');
      } else if (!resetResponse?.success) {
        setError(resetResponse?.error || 'Failed to send reset email');
      } else {
        // Store email for reference
        localStorage.setItem('resetEmail', email);

        console.log('Password reset requested:', resetResponse);

        if (resetResponse.emailSent) {
          console.log('N8N password reset email triggered successfully');
        } else {
          console.log('Password reset requested but email may not have been sent');
        }

        // Always go to step 2 for security (don't reveal if email exists)
        setCurrentStep(2);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  // Step 1: Enter Email
  if (currentStep === 1) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
          <div className="w-full max-w-md">
            <Card>
              <CardHeader className="text-center pb-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Lock className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl font-bold text-slate-900">
                  Forgot password?
                </CardTitle>
                <CardDescription className="text-slate-600 mt-2 px-4">
                  No worries, we'll send you reset instructions.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <form onSubmit={handleForgotPassword} className="space-y-6">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-11"
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-11 bg-slate-900 hover:bg-slate-800"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Reset password'
                    )}
                  </Button>
                </form>

                <div className="flex justify-center pt-6">
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/login')}
                    className="text-sm text-slate-600 hover:text-slate-900"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to login
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageTransition>
    );
  }

  // Step 2: Check Email
  if (currentStep === 2) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
          <div className="w-full max-w-md">
            <Card>
              <CardHeader className="text-center pb-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl font-bold text-slate-900">
                  Check your email
                </CardTitle>
                <CardDescription className="text-slate-600 mt-2 px-4">
                  We sent a password reset link to <strong>{email}</strong>
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="text-center space-y-4">
                  <p className="text-sm text-slate-600">
                    Didn't receive the email? Check your spam folder, or
                  </p>

                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep(1)}
                    className="w-full"
                  >
                    Try another email address
                  </Button>

                </div>

                <div className="flex justify-center pt-6">
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/login')}
                    className="text-sm text-slate-600 hover:text-slate-900"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to login
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageTransition>
    );
  }

  // Step 3 (Success) - renamed from Step 4
  return (
    <PageTransition>
      <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center pb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900">
                Password updated!
              </CardTitle>
              <CardDescription className="text-slate-600 mt-2 px-4">
                Your password has been successfully updated. You can now sign in with your new password.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <Button
                onClick={() => navigate('/login')}
                className="w-full h-11 bg-slate-900 hover:bg-slate-800"
              >
                Continue to login
              </Button>

              <div className="text-center">
                <p className="text-sm text-slate-600">
                  For security reasons, you'll need to sign in again with your new password.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageTransition>
  );
};

export default ForgotPasswordPages;